// Typed endpoint functions — the only place URL paths appear in the frontend.
import { request } from './api';
import type {
  AIStatus, AskResponse, AssumptionsResponse, CompareResult, Grade, Meta, MonteCarloResult, ReadyStatus, RunRequest, RunResult,
  ScenarioDetail, ScenarioInput, ScenarioSummary, SensitivityResult, SummaryResponse, TornadoResult,
} from '../types/api';

const HEAVY = 120_000;
const AI = 200_000; // free-tier reasoning models can take minutes; the backend bounds this too

export const endpoints = {
  ready: () => fetch('/ready', { cache: 'no-store' }).then(async (r) => (await r.json()) as ReadyStatus),
  meta: (signal?: AbortSignal) => request<Meta>('/meta', { signal }),
  assumptions: (params: { grade?: Grade; q?: string; prefix?: string }, signal?: AbortSignal) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
    return request<AssumptionsResponse>(`/assumptions${qs ? `?${qs}` : ''}`, { signal });
  },
  run: (body: RunRequest, signal?: AbortSignal) => request<RunResult>('/plant/run', { method: 'POST', body, signal }),

  sensitivity: (body: { scenario: ScenarioInput; x_axis: string; x_values: number[]; y_axis: string; y_values: number[]; include_breakeven?: boolean }, signal?: AbortSignal) =>
    request<SensitivityResult>('/analytics/sensitivity', { method: 'POST', body, signal, timeoutMs: HEAVY }),
  tornado: (body: { scenario: ScenarioInput; mode?: 'grade' | 'uniform'; uniform_pct?: number; top_n?: number }, signal?: AbortSignal) =>
    request<TornadoResult>('/analytics/tornado', { method: 'POST', body, signal, timeoutMs: HEAVY }),
  monteCarlo: (body: Record<string, unknown> & { scenario: ScenarioInput }, signal?: AbortSignal) =>
    request<MonteCarloResult>('/analytics/monte-carlo', { method: 'POST', body, signal, timeoutMs: HEAVY }),

  listScenarios: (signal?: AbortSignal) => request<ScenarioSummary[]>('/scenarios', { signal }),
  getScenario: (id: string, signal?: AbortSignal) => request<ScenarioDetail>(`/scenarios/${encodeURIComponent(id)}`, { signal }),
  saveScenario: (body: { name: string; description: string; run: RunRequest }) => request<ScenarioSummary>('/scenarios', { method: 'POST', body }),
  deleteScenario: (id: string) => request<void>(`/scenarios/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  compareScenarios: (ids: string[], signal?: AbortSignal) => request<CompareResult>(`/scenarios/compare?ids=${ids.map(encodeURIComponent).join(',')}`, { signal }),

  aiStatus: (signal?: AbortSignal) => request<AIStatus>('/ai/status', { signal }),
  aiSummary: (body: { run: RunRequest; use_llm: boolean }, signal?: AbortSignal) =>
    request<SummaryResponse>('/ai/summary', { method: 'POST', body, signal, timeoutMs: AI }),
  aiAsk: (body: { question: string; run: RunRequest; history: { role: 'user' | 'assistant'; content: string }[] }, signal?: AbortSignal) =>
    request<AskResponse>('/ai/ask', { method: 'POST', body, signal, timeoutMs: AI }),
};
